import React, { useState, useEffect } from 'react';
import { AddDesignationOffcanvas } from './add';
import { DeleteDesignationModal } from './delete';
import { EditDesignationOffcanvas } from './edit';
import { DesignationsList } from './list';
import api from '../../libs/axios';
import { useDebounce } from '../../hooks/useDebounce';

interface Designation {
    id: number;
    name: string;
    is_active: boolean;
    show_in_reporting_person_list?: boolean;
    created_at: string;
    updated_at: string;
    created_by: number;
    updated_by?: number;
    suspended_by?: number;
    suspended_at?: string;
}

interface Pagination {
    offset: number;
    limit: number;
    page: number;
    total_pages: number;
    total_count: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

interface DesignationFormData {
    name: string;
    status?: 'ACTIVE' | 'SUSPENDED';
    show_in_reporting_person_list: boolean;
}

interface EditDesignationFormData extends DesignationFormData {
    id: number;
}

export const DesignationsManagement: React.FC = () => {
    // State management
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        offset: 0,
        limit: 10,
        page: 1,
        total_pages: 1,
        total_count: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null
    });

    // Loading states
    const [designationsLoading, setDesignationsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal/Offcanvas states
    const [showAddDesignation, setShowAddDesignation] = useState(false);
    const [showEditDesignation, setShowEditDesignation] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDesignation, setSelectedDesignation] = useState<Designation | null>(null);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
        field: 'created_at',
        direction: 'DESC'
    });

    // Debounced search — API fires only after 400 ms of inactivity
    const debouncedSearch = useDebounce(searchTerm, 400);

    // API Functions
    const fetchDesignations = async (
        search: string = '',
        status: string = '',
        page: number = 1,
        limit?: number,
        sort = sortConfig
    ) => {
        setDesignationsLoading(true);
        try {
            const queryParams: any = {
                page: page,
                limit: limit || pagination.limit || 10
            };

            if (search.trim()) {
                queryParams.search = search;
            }

            if (status.trim()) {
                queryParams.status = status;
            }

            if (sort?.field) {
                queryParams.sort_by = sort.field;
                queryParams.sort_order = sort.direction;
            }

            const response = await api.get('/designations/list', {
                params: queryParams
            });

            if (response.data.success) {
                setDesignations(response.data.data.designations || []);
                setPagination(response.data.data.pagination || pagination);
            } else {
                console.error('API returned success: false');
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
        } finally {
            setDesignationsLoading(false);
        }
    };

    const fetchDesignationById = async (designationId: number): Promise<Designation | null> => {
        try {
            const response = await api.get(`/designations/${designationId}`);

            if (response.data.success) {
                return response.data.data.designation;
            }
            return null;
        } catch (error) {
            console.error('Error fetching designation by ID:', error);
            return null;
        }
    };

    const createDesignation = async (designationData: DesignationFormData) => {
        setActionLoading(true);
        try {
            const { show_in_reporting_person_list, ...createPayload } = designationData;
            const response = await api.post('/designations/create', createPayload);

            if (response.data.success) {
                const createdDesignationId = response.data?.data?.designation?.id;

                if (typeof createdDesignationId === 'number') {
                    const reportingPersonResponse = await api.patch(
                        `/designations/${createdDesignationId}/reporting-person`,
                        {
                            show_in_reporting_person_list
                        }
                    );

                    if (!reportingPersonResponse.data.success) {
                        console.error('Failed to update reporting person list setting:', reportingPersonResponse.data.message);
                    }
                } else {
                    console.error('Failed to update reporting person list setting: designation id missing in create response');
                }

                await fetchDesignations();
                setShowAddDesignation(false);
                console.log('Designation created successfully');
            } else {
                console.error('Failed to create designation:', response.data.message);
            }

        } catch (error) {
            console.error('Error creating designation:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const updateDesignation = async (designationData: EditDesignationFormData) => {
        setActionLoading(true);
        try {
            const { show_in_reporting_person_list, ...updatePayload } = designationData;
            const response = await api.put(`/designations/${designationData.id}`, updatePayload);

            if (response.data.success) {
                const reportingPersonResponse = await api.patch(
                    `/designations/${designationData.id}/reporting-person`,
                    {
                        show_in_reporting_person_list
                    }
                );

                if (!reportingPersonResponse.data.success) {
                    console.error('Failed to update reporting person list setting:', reportingPersonResponse.data.message);
                }

                await fetchDesignations();
                setShowEditDesignation(false);
                setSelectedDesignation(null);
                console.log('Designation updated successfully');
            } else {
                console.error('Failed to update designation:', response.data.message);
            }
        } catch (error) {
            console.error('Error updating designation:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const deleteDesignation = async () => {
        if (!selectedDesignation) return;

        setActionLoading(true);
        try {
            const response = await api.delete(`/designations/${selectedDesignation.id}`);

            if (response.data.success) {
                await fetchDesignations();
                setShowDeleteModal(false);
                setSelectedDesignation(null);
                console.log('Designation deleted successfully');
            } else {
                console.error('Failed to delete designation:', response.data.message);
            }
        } catch (error) {
            console.error('Error deleting designation:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const toggleDesignationStatus = async (designation: Designation, newStatus: 'ACTIVE' | 'SUSPENDED') => {
        setActionLoading(true);
        try {
            const response = await api.patch(`/designations/${designation.id}/status`, {
                status: newStatus
            });

            if (response.data.success) {
                await fetchDesignations();
                console.log(`Designation ${newStatus.toLowerCase()} successfully`);
            } else {
                console.error('Failed to update designation status:', response.data.message);
            }
        } catch (error) {
            console.error('Error updating designation status:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const toggleReportingPersonListSetting = async (designation: Designation, showInList: boolean) => {
        setActionLoading(true);
        try {
            const response = await api.patch(`/designations/${designation.id}/reporting-person`, {
                show_in_reporting_person_list: showInList
            });

            if (response.data.success) {
                await fetchDesignations();
                console.log('Reporting person list setting updated successfully');
            } else {
                console.error('Failed to update reporting person list setting:', response.data.message);
            }
        } catch (error) {
            console.error('Error updating reporting person list setting:', error);
        } finally {
            setActionLoading(false);
        }
    };

    // Event handlers
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        // Actual API call is triggered by the debouncedSearch effect below
    };

    // Fire fetch when debounced search value settles
    useEffect(() => {
        fetchDesignations(debouncedSearch, statusFilter, 1, undefined, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const handleStatusFilter = (status: string) => {
        setStatusFilter(status);
        fetchDesignations(searchTerm, status, 1, undefined, sortConfig);
    };

    const handlePageChange = (page: number) => {
        fetchDesignations(searchTerm, statusFilter, page, undefined, sortConfig);
    };

    const handleLimitChange = (limit: number) => {
        setPagination((prev) => ({ ...prev, limit, page: 1 }));
        fetchDesignations(searchTerm, statusFilter, 1, limit, sortConfig);
    };

    const handleAddDesignation = () => {
        setShowAddDesignation(true);
    };

    const handleEditDesignation = async (designation: Designation) => {
        const fullDesignation = await fetchDesignationById(designation.id);
        if (fullDesignation) {
            setSelectedDesignation(fullDesignation);
            setShowEditDesignation(true);
        }
    };

    const handleDeleteDesignation = (designation: Designation) => {
        setSelectedDesignation(designation);
        setShowDeleteModal(true);
    };

    const handleToggleStatus = (designation: Designation) => {
        const newStatus = designation.is_active ? 'SUSPENDED' : 'ACTIVE';
        toggleDesignationStatus(designation, newStatus);
    };

    const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
        const sort = { field, direction };
        setSortConfig(sort);
        fetchDesignations(searchTerm, statusFilter, pagination.page, undefined, sort);
    };

    const handleCloseModals = () => {
        setShowAddDesignation(false);
        setShowEditDesignation(false);
        setShowDeleteModal(false);
        setSelectedDesignation(null);
    };

    // Initialize data on component mount
    useEffect(() => {
        fetchDesignations();
    }, []);

    return (
        <div className="">
            <div className="">
                {/* Main Designations List */}
                <DesignationsList
                    designations={designations}
                    pagination={pagination}
                    onSearch={handleSearch}
                    onStatusFilter={handleStatusFilter}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    onAddDesignation={handleAddDesignation}
                    onEditDesignation={handleEditDesignation}
                    onDeleteDesignation={handleDeleteDesignation}
                    onToggleStatus={handleToggleStatus}
                    onToggleReportingPersonList={toggleReportingPersonListSetting}
                    onSort={handleSort}
                    loading={designationsLoading}
                    searchValue={searchTerm}
                    statusValue={statusFilter}
                    sortState={sortConfig}
                />

                {/* Add Designation Offcanvas */}
                <AddDesignationOffcanvas
                    isOpen={showAddDesignation}
                    onClose={handleCloseModals}
                    onSubmit={createDesignation}
                    loading={actionLoading}
                />

                {/* Edit Designation Offcanvas */}
                <EditDesignationOffcanvas
                    isOpen={showEditDesignation}
                    onClose={handleCloseModals}
                    onSubmit={updateDesignation}
                    designation={selectedDesignation}
                    loading={actionLoading}
                />

                {/* Delete Designation Modal */}
                <DeleteDesignationModal
                    isOpen={showDeleteModal}
                    onClose={handleCloseModals}
                    onDelete={deleteDesignation}
                    text={
                        selectedDesignation
                            ? `Are you sure you want to delete the designation "${selectedDesignation.name}"? This action cannot be undone.`
                            : ''
                    }
                    loading={actionLoading}
                />
            </div>
        </div>
    );
};
